import React, { useState, useEffect } from 'react';
import { Website, BackupSlotWebsite, BackupSlot, TimeSlot, Contact, CommentEntry, BillingPeriodicity, PaymentStatus } from '../../types';
import { ArrowPathIcon, TrashIcon, UserPlusIcon, TagIcon, PlusIcon, EyeIcon, EyeSlashIcon, ExclamationCircleIcon, InformationCircleIcon, ClipboardDocumentIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { backupService, bucketService } from '../../services/api';
import CategorizedComments from './CategorizedComments';

interface WebsiteFormProps {
  website?: Website;
  onSubmit: (website: Website) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

// Site web vide pour la création d'un nouveau site
const emptyWebsite: Website = {
  id: '',
  clientName: '',
  url: '',
  platform: '',
  creationDate: '',
  anniversaryDate: '',
  hostingType: '',
  hostingProvider: '',
  ipAddress: '',
  serverConfig: '',
  sslExpiration: '',
  contractType: '',
  amount: 0,
  lastBillingDate: '',
  nextBillingDate: '',
  paymentStatus: '',
  billingPeriodicity: 'mensuel',
  billingPeriodicityDays: 30,
  domain: '',
  domainExpiration: '',
  registrar: '',
  dnsConfig: {
    cloudflareProxy: false,
    cacheHeaders: false
  },
  htaccessEnabled: false,
  htaccessUsername: '',
  htaccessPassword: '',
  preprodUrl: '',
  emailType: 'smtp', // Type d'email par défaut
  emailNote: '',
  smtpServer: '',
  smtpPassword: '',
  smtpEmail: '',
  smtpPort: 0,
  encryption: '',
  postalHttpUrl: '',
  postalHttpApiKey: '',
  postalHttpSenderEmail: '',
  captchaType: '', // Anciennement authType
  bucketName: '',
  status: 'active',
  backupType: 'minio',
  backupNote: '',
  backupFrequency: '',
  maintenanceComments: '',
  cookiesManagement: '',
  primaryContact: '',
  contactEmail: '',
  contactPhone: '',
  comments: '',
  categorizedComments: {
    technique: [],
    clients: [],
    facturation: [],
    divers: []
  },
  // Nouveau tableau de contacts
  contacts: [],
  // Champs pour la compatibilité
  emailAddresses: [],
  authType: '',
  
  // Système de tags
  tags: []
};

// Composant pour gérer un contact individuel
interface ContactFormProps {
  contact: Contact;
  onUpdate: (updatedContact: Contact) => void;
  onDelete: () => void;
  isPrimary: boolean;
  onSetPrimary: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ contact, onUpdate, onDelete, isPrimary, onSetPrimary }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    onUpdate({
      ...contact,
      [name]: value
    });
  };

  return (
    <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-md mb-3">
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center space-x-2">
          <input
            type="radio"
            name="primaryContact"
            checked={isPrimary}
            onChange={onSetPrimary}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Contact principal
          </span>
        </div>
        <button
          type="button"
          onClick={onDelete}
          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nom
          </label>
          <input
            type="text"
            name="name"
            value={contact.name}
            onChange={handleChange}
            className="input-field"
            placeholder="Nom du contact"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={contact.email}
            onChange={handleChange}
            className="input-field"
            placeholder="email@example.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Téléphone
          </label>
          <input
            type="tel"
            name="phone"
            value={contact.phone}
            onChange={handleChange}
            className="input-field"
            placeholder="+33 1 23 45 67 89"
          />
        </div>
      </div>
    </div>
  );
};

// Options pour les menus déroulants
const BILLING_PERIODICITY_OPTIONS = [
  { value: 'non_specifie', label: 'Non spécifié' },
  { value: 'mensuel', label: 'Mensuel' },
  { value: 'trimestriel', label: 'Trimestriel' },
  { value: 'semestriel', label: 'Semestriel' },
  { value: 'annuel', label: 'Annuel' },
  { value: 'autre', label: 'Autre (personnalisé)' }
];

const PAYMENT_STATUS_OPTIONS = [
  { value: 'payé', label: 'Payé' },
  { value: 'en_attente', label: 'En attente' },
  { value: 'en_retard', label: 'En retard' },
  { value: 'annulé', label: 'Annulé' },
  { value: 'remboursé', label: 'Remboursé' }
];

const WebsiteForm: React.FC<WebsiteFormProps> = ({ website = emptyWebsite, onSubmit, onCancel, isSubmitting = false }) => {
  const [formData, setFormData] = useState<Website>(website);
  const [activeTab, setActiveTab] = useState<'general' | 'hosting' | 'billing' | 'domain' | 'email' | 'maintenance' | 'contacts' | 'tags' | 'contact'>('general');
  const [newTag, setNewTag] = useState('');
  const [availableSlots, setAvailableSlots] = useState<BackupSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState<boolean>(false);
  const [selectedSlot, setSelectedSlot] = useState<string>('');
  const [specificTime, setSpecificTime] = useState<string>('');
  const [availableTimes, setAvailableTimes] = useState<TimeSlot[]>([]);
  
  // États pour la création de bucket MinIO
  const [creatingBucket, setCreatingBucket] = useState<boolean>(false);
  const [bucketConfig, setBucketConfig] = useState<any>(null);
  const [showBucketCredentials, setShowBucketCredentials] = useState<boolean>(false);
  const [bucketError, setBucketError] = useState<string | null>(null);
  const [bucketLogs, setBucketLogs] = useState<any[]>([]);
  const [showLogsModal, setShowLogsModal] = useState<boolean>(false);

  // Fonction pour calculer la prochaine date de facturation
  const calculateNextBillingDate = (referenceDate: string, periodicity: BillingPeriodicity, periodicityDays?: number): string => {
    if (!referenceDate) return '';
    
    const refDate = new Date(referenceDate);
    const nextDate = new Date(refDate);
    
    switch (periodicity) {
      case 'mensuel':
        nextDate.setMonth(nextDate.getMonth() + 1);
        break;
      case 'trimestriel':
        nextDate.setMonth(nextDate.getMonth() + 3);
        break;
      case 'semestriel':
        nextDate.setMonth(nextDate.getMonth() + 6);
        break;
      case 'annuel':
        nextDate.setFullYear(nextDate.getFullYear() + 1);
        break;
      case 'autre':
        if (periodicityDays) {
          nextDate.setDate(nextDate.getDate() + periodicityDays);
        }
        break;
    }
    
    return nextDate.toISOString().split('T')[0];
  };

  // Fonction pour calculer la prochaine facturation basée sur l'anniversaire
  const calculateNextBillingFromAnniversary = (): string => {
    if (!formData.anniversaryDate || !formData.billingPeriodicity || formData.billingPeriodicity === 'non_specifie') {
      return '';
    }
    
    const anniversary = new Date(formData.anniversaryDate);
    const today = new Date();
    const currentYear = today.getFullYear();
    
    // Calculer la prochaine date d'anniversaire
    let nextAnniversary = new Date(anniversary);
    nextAnniversary.setFullYear(currentYear);
    
    // Si l'anniversaire de cette année est déjà passé, prendre l'année suivante
    if (nextAnniversary < today) {
      nextAnniversary.setFullYear(currentYear + 1);
    }
    
    // Calculer la prochaine facturation selon la périodicité
    return calculateNextBillingDate(nextAnniversary.toISOString().split('T')[0], formData.billingPeriodicity, formData.billingPeriodicityDays);
  };

  // Fonction pour mettre à jour la prochaine date de facturation
  const updateNextBillingDate = () => {
    if (!formData.billingPeriodicity || formData.billingPeriodicity === 'non_specifie') {
      return; // Ne rien faire si pas de périodicité définie
    }
    
    if (formData.anniversaryDate) {
      // Utiliser la date d'anniversaire comme référence
      const nextDate = calculateNextBillingFromAnniversary();
      setFormData(prev => ({ ...prev, nextBillingDate: nextDate }));
    } else if (formData.lastBillingDate) {
      // Fallback sur la dernière facturation si pas d'anniversaire
      const nextDate = calculateNextBillingDate(
        formData.lastBillingDate, 
        formData.billingPeriodicity, 
        formData.billingPeriodicityDays
      );
      setFormData(prev => ({ ...prev, nextBillingDate: nextDate }));
    }
  };

  // Fonction pour marquer comme facturé
  const markAsBilled = () => {
    const today = new Date().toISOString().split('T')[0];
    let nextDate = '';
    
    if (formData.billingPeriodicity && formData.billingPeriodicity !== 'non_specifie') {
      if (formData.anniversaryDate) {
        // Utiliser la date d'anniversaire comme référence
        nextDate = calculateNextBillingFromAnniversary();
      } else {
        // Fallback sur la date actuelle
        nextDate = calculateNextBillingDate(
          today, 
          formData.billingPeriodicity, 
          formData.billingPeriodicityDays
        );
      }
    }
    
    // Ajouter à l'historique des paiements
    const paymentEntry = {
      id: Date.now().toString(),
      date: today,
      status: 'payé' as PaymentStatus,
      amount: formData.amount || 0,
      notes: 'Marqué comme facturé'
    };
    
    setFormData(prev => ({
      ...prev,
      lastBillingDate: today,
      nextBillingDate: nextDate,
      paymentStatus: 'payé',
      paymentHistory: [...(prev.paymentHistory || []), paymentEntry]
    }));
  };

  // Initialiser les contacts à partir de l'ancien format si nécessaire
  useEffect(() => {
    if (website && !website.contacts?.length && (website.primaryContact || website.contactEmail || website.contactPhone)) {
      const initialContacts: Contact[] = [];
      if (website.primaryContact || website.contactEmail || website.contactPhone) {
        initialContacts.push({
          id: Math.random().toString(36).substring(2, 9),
          name: website.primaryContact || '',
          email: website.contactEmail || '',
          phone: website.contactPhone || '',
          isPrimary: true
        });
      }
      
      setFormData(prevData => ({
        ...prevData,
        contacts: initialContacts
      }));
    }
  }, [website]);

  // Initialiser les commentaires catégorisés à partir de l'ancien champ comments
  useEffect(() => {
    if (website && website.comments && !website.categorizedComments) {
      const migratedComment: CommentEntry = {
        id: Date.now().toString(),
        text: website.comments,
        date: new Date().toISOString(),
        author: 'Migration automatique'
      };

      setFormData(prevData => ({
        ...prevData,
        categorizedComments: {
          technique: [],
          clients: [],
          facturation: [],
          divers: [migratedComment] // Mettre l'ancien commentaire dans "divers"
        }
      }));
    } else if (website && !website.categorizedComments) {
      // Initialiser avec une structure vide si pas de commentaires catégorisés
      setFormData(prevData => ({
        ...prevData,
        categorizedComments: {
          technique: [],
          clients: [],
          facturation: [],
          divers: []
        }
      }));
    }
  }, [website]);

  const handleCategorizedCommentsChange = (comments: {
    technique: CommentEntry[];
    clients: CommentEntry[];
    facturation: CommentEntry[];
    divers: CommentEntry[];
  }) => {
    setFormData(prevData => ({
      ...prevData,
      categorizedComments: comments
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    // Gérer les différents types de champs
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setFormData({
        ...formData,
        [name]: checkbox.checked
      });
    } else if (type === 'number') {
      setFormData({
        ...formData,
        [name]: parseFloat(value) || 0
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleDnsConfigChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      dnsConfig: {
        ...formData.dnsConfig,
        [name]: checked
      }
    });
  };

  // Fonctions pour gérer les contacts
  const handleAddContact = () => {
    // Créer un nouveau contact
    const newContact: Contact = {
      id: Math.random().toString(36).substring(2, 9),
      name: '',
      email: '',
      phone: '',
      isPrimary: formData.contacts?.length === 0 // Premier contact est automatiquement le principal
    };
    
    // Ajouter le nouveau contact à la liste
    setFormData(prevData => ({
      ...prevData,
      contacts: [...(prevData.contacts || []), newContact]
    }));
  };
  
  const handleUpdateContact = (updatedContact: Contact) => {
    setFormData(prevData => ({
      ...prevData,
      contacts: prevData.contacts?.map(contact => 
        contact.id === updatedContact.id ? updatedContact : contact
      ) || []
    }));
  };
  
  const handleDeleteContact = (contactId: string) => {
    const updatedContacts = formData.contacts?.filter(contact => contact.id !== contactId) || [];
    
    // Si on supprime le contact principal, définir le premier contact restant comme principal
    if (formData.contacts?.find(c => c.id === contactId)?.isPrimary && updatedContacts.length > 0) {
      updatedContacts[0].isPrimary = true;
    }
    
    setFormData(prevData => ({
      ...prevData,
      contacts: updatedContacts
    }));
  };
  
  const handleSetPrimaryContact = (contactId: string) => {
    setFormData(prevData => ({
      ...prevData,
      contacts: prevData.contacts?.map(contact => ({
        ...contact,
        isPrimary: contact.id === contactId
      })) || []
    }));
  };

  // Fonctions pour gérer les tags
  const handleAddTag = () => {
    if (newTag.trim() && !(formData.tags || []).includes(newTag.trim())) {
      setFormData(prevData => ({
        ...prevData,
        tags: [...(prevData.tags || []), newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prevData => ({
      ...prevData,
      tags: (prevData.tags || []).filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Trouver le contact principal pour la compatibilité
    const primaryContact = formData.contacts?.find(contact => contact.isPrimary);
    
    // Mettre à jour les données avant soumission
    const submissionData = {
      ...formData,
      // Mettre à jour les anciens champs pour la compatibilité
      primaryContact: primaryContact?.name || formData.primaryContact,
      contactEmail: primaryContact?.email || formData.contactEmail,
      contactPhone: primaryContact?.phone || formData.contactPhone
    };
    
    // Générer un ID aléatoire si c'est un nouveau site
    const finalData = submissionData.id 
      ? submissionData 
      : { ...submissionData, id: Math.random().toString(36).substring(2, 9) };
      
    onSubmit(finalData);
  };

  // Charger les tranches horaires disponibles
  useEffect(() => {
    if (formData.backupType === 'minio') {
      setLoadingSlots(true);
      // Obtenir le rapport des tranches horaires
      backupService.getBackupSlotsReport()
        .then(data => {
          const report = data.report;
          // Transformer les données du rapport en format compatible
          const formattedSlots = report.usageBySlot.map((slot: any) => ({
            id: `slot-${slot.slot}`, // Générer un ID artificiel
            slot: slot.slot,
            time: slot.time,
            websites: slot.websites,
            websiteCount: slot.websiteCount,
            availableTimes: generateAvailableTimesForSlot(slot)
          }));
          setAvailableSlots(formattedSlots);
          setLoadingSlots(false);
        })
        .catch(error => {
          console.error('Erreur lors du chargement du rapport des tranches horaires:', error);
          setLoadingSlots(false);
        });
    }
  }, [formData.backupType]);

  // Fonction pour générer les horaires disponibles pour une tranche
  const generateAvailableTimesForSlot = (slot: any) => {
    // Heures de backup (21h à 6h)
    const hours = [21, 22, 23, 0, 1, 2, 3, 4, 5, 6];
    // Offset en minutes pour cette tranche
    const getMinuteOffsetForSlot = (slotNumber: number): number => {
      const offsetMap: {[key: number]: number} = {
        1: 5,   2: 10,  3: 15,  4: 20,  5: 25,  6: 30,  7: 35,
        8: 40,  9: 45,  10: 50, 11: 55, 12: 0,  13: 2,  14: 7,  
        15: 12, 16: 17, 17: 22, 18: 27, 19: 32, 20: 37, 21: 42, 
        22: 47, 23: 52, 24: 57, 25: 1,  26: 3,  27: 11, 28: 13
      };
      return offsetMap[slotNumber] || 0;
    };

    const minuteOffset = getMinuteOffsetForSlot(slot.slot);
    
    // Générer tous les horaires possibles pour cette tranche
    const allPossibleTimes = hours.map(hour => {
      const formattedHour = hour.toString().padStart(2, '0');
      const formattedMinute = minuteOffset.toString().padStart(2, '0');
      return {
        time: `${formattedHour}:${formattedMinute}`
      };
    });

    // Horaires déjà utilisés par les sites de cette tranche
    const usedTimes = slot.websites.map((website: any) => website.specificTime);
    
    // Filtrer les horaires disponibles (ceux qui ne sont pas utilisés)
    return allPossibleTimes.filter(timeSlot => !usedTimes.includes(timeSlot.time));
  };

  // Fonction auxiliaire pour obtenir le décalage en minutes pour une tranche
  const getOffsetInMinutes = (slotNumber: number): number => {
    const offsetMap: {[key: number]: number} = {
      1: 5,   2: 10,  3: 15,  4: 20,  5: 25,  6: 30,  7: 35,
      8: 40,  9: 45,  10: 50, 11: 55, 12: 0,  13: 2,  14: 7,  
      15: 12, 16: 17, 17: 22, 18: 27, 19: 32, 20: 37, 21: 42, 
      22: 47, 23: 52, 24: 57, 25: 1,  26: 3,  27: 11, 28: 13
    };
    
    return offsetMap[slotNumber] || 0;
  };

  // Fonction pour créer un bucket MinIO
  const handleCreateBucket = async () => {
    if (!formData.clientName) {
      setBucketError('Le nom du client est requis pour créer un bucket');
      return;
    }

    setCreatingBucket(true);
    setBucketError(null);
    setBucketConfig(null);
    setBucketLogs([]);

    try {
      // Générer le nom du bucket basé sur le nom du client
      const baseName = formData.clientName
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Supprimer les accents
        .replace(/[^a-z0-9]/g, '-') // Remplacer les caractères spéciaux par des tirets
        .replace(/-+/g, '-') // Remplacer les tirets multiples par un seul
        .replace(/^-|-$/g, ''); // Supprimer les tirets en début/fin

      const bucketName = `up-${baseName}`;

      // Utiliser la nouvelle API pour créer le bucket automatiquement pour le site web
      const result = formData.id 
        ? await bucketService.createBucketForWebsite(formData.id)
        : await bucketService.createBucket(bucketName);
      
      if (result.success) {
        // Mettre à jour le nom du bucket dans le formulaire
        setFormData(prev => ({
          ...prev,
          bucketName: result.bucketName,
          backupType: 'minio'
        }));
        
        // Afficher un message de succès avec toutes les informations
        setBucketConfig({
          bucketName: result.bucketName,
          message: result.message,
          existed: result.existed,
          // Nouvelles informations du Service Account
          accessKey: result.config?.accessKey,
          secretKey: result.config?.secretKey,
          endpoint: result.config?.endpoint,
          bucket: result.config?.bucket,
          logs: result.logs
        });
        setShowBucketCredentials(true); // Afficher les credentials
        setShowLogsModal(true);
      } else {
        setBucketError(result.message || 'Erreur lors de la création du bucket');
        setShowLogsModal(true);
      }
    } catch (error: any) {
      console.error('Erreur lors de la création du bucket:', error);
      setBucketError(error.response?.data?.message || 'Erreur lors de la création du bucket');
    } finally {
      setCreatingBucket(false);
    }
  };

  // Fonction pour copier dans le presse-papier
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // Optionnel : afficher une notification de succès
      console.log(`${label} copié dans le presse-papier`);
    } catch (error) {
      console.error('Erreur lors de la copie:', error);
    }
  };

  // Fonction pour générer le nom du bucket depuis l'URL
  const generateBucketName = () => {
    try {
      if (formData.url) {
        const url = new URL(formData.url);
        const domain = url.hostname.replace('www.', '');
        const siteName = domain.split('.')[0];
        setFormData({
          ...formData,
          bucketName: `up-${siteName}`
        });
      }
    } catch (error) {
      console.error("URL invalide", error);
    }
  };

  // Remplacer le champ de tranche par un sélecteur des tranches disponibles
  const backupSlotContent = (
    <div>
      <label htmlFor="backupSlot" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Tranche
      </label>
      {loadingSlots ? (
        <div className="flex items-center space-x-2">
          <ArrowPathIcon className="animate-spin h-5 w-5 text-primary-500" />
          <span className="text-sm">Chargement des tranches...</span>
        </div>
      ) : (
        <select
          id="backupSlot"
          name="backupSlot"
          value={formData.backupSchedule?.DayOfMonth || ''}
          onChange={(e) => {
            const slotNumber = parseInt(e.target.value);
            const selectedSlot = availableSlots.find(slot => slot.slot === slotNumber);
            
            // Si l'utilisateur sélectionne une tranche
            if (slotNumber && selectedSlot) {
              // Vérifier si une heure spécifique a été sélectionnée via l'attribut data-time
              const selectedOption = e.target.options[e.target.selectedIndex];
              const timeAttribute = selectedOption.getAttribute('data-time');
              
              // Déterminer l'heure à utiliser
              let time = selectedSlot.time;
              
              // Si un attribut time est fourni, l'utiliser
              if (timeAttribute) {
                time = timeAttribute;
              }
              
              // Mettre à jour avec la nouvelle structure
              setFormData({
                ...formData,
                backupSchedule: {
                  DayOfMonth: selectedSlot.slot,
                  time: time
                }
              });
            } else {
              // Option "Sélectionner..." choisie
              setFormData({
                ...formData,
                backupSchedule: undefined
              });
            }
          }}
          className="input-field"
        >
          <option value="">Sélectionner une tranche horaire...</option>
          
          {/* Option pour la tranche actuelle si le site en a déjà une */}
          {formData.backupSchedule?.DayOfMonth && formData.backupSchedule?.time && (
            <option value={formData.backupSchedule.DayOfMonth}>
              Actuelle: Tranche {formData.backupSchedule.DayOfMonth} - {formData.backupSchedule.time}
            </option>
          )}
          
          {/* Afficher toutes les tranches disponibles */}
          {availableSlots
            // Trier les tranches par numéro de tranche (ordre croissant)
            .sort((a, b) => a.slot - b.slot)
            // Filtrer pour ne garder que les tranches qui ont des horaires disponibles
            .filter(slot => slot.availableTimes && slot.availableTimes.length > 0)
            // Afficher les tranches avec leurs horaires disponibles
            .map(slot => {
              const isAssignedToCurrentSite = formData.id && slot.websites.some((w: BackupSlotWebsite) => w.id === formData.id);
              
              // Si la tranche est assignée au site actuel, afficher uniquement l'option pour l'horaire actuel
              // et tous les autres horaires disponibles dans cette tranche
              if (isAssignedToCurrentSite) {
                // Extraire les horaires actuellement utilisés dans cette tranche par d'autres sites
                const usedHoraires = slot.websites
                  .filter((w: BackupSlotWebsite) => w.id !== formData.id) // Exclure le site actuel
                  .map((w: BackupSlotWebsite) => w.specificTime);
                
                // Générer tous les horaires possibles pour cette tranche
                const hours = [21, 22, 23, 0, 1, 2, 3, 4, 5, 6];
                const allHoraires = hours.map(hour => {
                  const formattedHour = hour.toString().padStart(2, '0');
                  const formattedMinute = getOffsetInMinutes(slot.slot).toString().padStart(2, '0');
                  return `${formattedHour}:${formattedMinute}`;
                });
                
                // Filtrer pour ne garder que les horaires non utilisés par d'autres sites
                const availableHoraires = allHoraires.filter(horaire => !usedHoraires.includes(horaire));
                
                return availableHoraires.map((horaire, index) => {
                  const isCurrentHoraire = horaire === formData.backupSchedule?.time;
                  return (
                    <option 
                      key={`current-${slot.slot}-${index}`} 
                      value={slot.slot}
                      data-time={horaire}
                      selected={isCurrentHoraire}
                    >
                      Tranche {slot.slot} (+{getOffsetInMinutes(slot.slot)}min) - {horaire} {isCurrentHoraire ? '(actuel)' : ''}
                    </option>
                  );
                });
              }
              
              // Afficher toutes les horaires disponibles pour chaque tranche
              if (slot.availableTimes && slot.availableTimes.length > 0) {
                // Créer les options pour chaque horaire disponible dans cette tranche
                return slot.availableTimes.map((timeSlot: TimeSlot, index: number) => (
                  <option 
                    key={`${slot.slot}-${index}`} 
                    value={slot.slot}
                    data-time={timeSlot.time}
                  >
                    Tranche {slot.slot} (+{getOffsetInMinutes(slot.slot)}min) - {timeSlot.time}
                  </option>
                ));
              }
              
              return null; // Ne devrait jamais se produire à cause du filtre ci-dessus
            })}
        </select>
      )}
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      {/* Onglets */}
      <div className="mb-8 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          <button
            type="button"
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'general'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('general')}
          >
            Informations générales
          </button>
          <button
            type="button"
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'hosting'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('hosting')}
          >
            Hébergement
          </button>
          <button
            type="button"
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'billing'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('billing')}
          >
            Facturation
          </button>
          <button
            type="button"
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'domain'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('domain')}
          >
            Domaine et DNS
          </button>
          <button
            type="button"
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'email'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('email')}
          >
            Emails et SMTP
          </button>
          <button
            type="button"
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'maintenance'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('maintenance')}
          >
            Maintenance
          </button>
          <button
            type="button"
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'contact'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('contact')}
          >
            Contacts
          </button>
          <button
            type="button"
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'tags'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('tags')}
          >
            <TagIcon className="h-4 w-4 inline mr-1" />
            Tags
          </button>
        </nav>
      </div>

      {/* Contenu des onglets */}
      <div className="card">
        {/* Informations générales */}
        {activeTab === 'general' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="clientName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nom du client *
                </label>
                <input
                  type="text"
                  id="clientName"
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleChange}
                  required
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  URL *
                </label>
                <input
                  type="url"
                  id="url"
                  name="url"
                  value={formData.url}
                  onChange={(e) => {
                    handleChange(e);
                    // Générer automatiquement le nom de domaine à partir de l'URL
                    try {
                      if (e.target.value) {
                        const url = new URL(e.target.value);
                        const domain = url.hostname.replace('www.', '');
                        setFormData(prev => ({
                          ...prev,
                          domain: domain
                        }));
                      }
                    } catch (error) {
                      console.error("URL invalide", error);
                    }
                  }}
                  required
                  className="input-field"
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label htmlFor="preprodUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  URL de préproduction
                </label>
                <input
                  type="url"
                  id="preprodUrl"
                  name="preprodUrl"
                  value={formData.preprodUrl}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="https://preprod.example.com"
                />
              </div>
              <div>
                <label htmlFor="platform" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Plateforme
                </label>
                <select
                  id="platform"
                  name="platform"
                  value={formData.platform}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="">Non spécifié</option>
                  <option value="WordPress">WordPress</option>
                  <option value="Prestashop">Prestashop</option>
                  <option value="Snipelt">Snipelt</option>
                  <option value="Custom">Custom</option>
                  <option value="Galette">Galette</option>
                  <option value="Symfony">Symfony</option>
                  <option value="Shopify">Shopify</option>
                  <option value="Drupal">Drupal</option>
                </select>
              </div>
              <div>
                <label htmlFor="creationDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date de création
                </label>
                <input
                  type="date"
                  id="creationDate"
                  name="creationDate"
                  value={formData.creationDate}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="anniversaryDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date d'anniversaire
                </label>
                <input
                  type="date"
                  id="anniversaryDate"
                  name="anniversaryDate"
                  value={formData.anniversaryDate}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  État du contrat
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="active">Actif</option>
                  <option value="dormant">En sommeil</option>
                  <option value="terminated">Terminé</option>
                  <option value="production">En production</option>
                  <option value="internal">Projet Interne</option>
                </select>
              </div>
              <div>
                <label htmlFor="cookiesManagement" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Gestion des cookies
                </label>
                <select
                  id="cookiesManagement"
                  name="cookiesManagement"
                  value={formData.cookiesManagement}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="">Non spécifié</option>
                  <option value="cookieyes">CookieYes</option>
                  <option value="presendium">Presendium</option>
                  <option value="complianz">Complianz</option>
                  <option value="cookie-notice">Cookie Notice</option>
                  <option value="gdpr-cookie-compliance">GDPR Cookie Compliance (Moove)</option>
                  <option value="iubenda">Iubenda</option>
                  <option value="cookie-law-info">Cookie Law Info (WebToffee)</option>
                  <option value="non necessaire">Non nécessaire</option>
                  <option value="pas fait">Pas fait</option>
                </select>
              </div>
              <div>
                <label htmlFor="captchaType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type de Captcha
                </label>
                <select
                  id="captchaType"
                  name="captchaType"
                  value={formData.captchaType}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="">Non spécifié</option>
                  <option value="Turnstile">Turnstile</option>
                  <option value="reCAPTCHA">reCAPTCHA</option>
                  <option value="aucun">Aucun</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Commentaires
              </label>
              <CategorizedComments
                comments={formData.categorizedComments || {
                  technique: [],
                  clients: [],
                  facturation: [],
                  divers: []
                }}
                onChange={handleCategorizedCommentsChange}
              />
            </div>
          </div>
        )}

        {/* Hébergement */}
        {activeTab === 'hosting' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="hostingType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type d'hébergement
                </label>
                <input
                  type="text"
                  id="hostingType"
                  name="hostingType"
                  value={formData.hostingType}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="VPS, Mutualisé, etc."
                />
              </div>
              <div>
                <label htmlFor="hostingProvider" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Fournisseur d'hébergement
                </label>
                <input
                  type="text"
                  id="hostingProvider"
                  name="hostingProvider"
                  value={formData.hostingProvider}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="OVH, O2Switch, etc."
                />
              </div>
              <div>
                <label htmlFor="ipAddress" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Adresse IP
                </label>
                <input
                  type="text"
                  id="ipAddress"
                  name="ipAddress"
                  value={formData.ipAddress}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="serverConfig" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Configuration du serveur
                </label>
                <input
                  type="text"
                  id="serverConfig"
                  name="serverConfig"
                  value={formData.serverConfig}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Dockerisé, PHP 8.2, etc."
                />
              </div>
              <div>
                <label htmlFor="sslExpiration" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Expiration SSL
                </label>
                <input
                  type="date"
                  id="sslExpiration"
                  name="sslExpiration"
                  value={formData.sslExpiration}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
            </div>
          </div>
        )}

        {/* Facturation */}
        {activeTab === 'billing' && (
          <div className="space-y-6">
            {/* Actions rapides */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-3">Actions rapides</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={markAsBilled}
                  className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md transition-colors"
                >
                  Marquer comme facturé aujourd'hui
                </button>
                <button
                  type="button"
                  onClick={updateNextBillingDate}
                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md transition-colors"
                >
                  Recalculer prochaine facturation
                </button>
                {formData.anniversaryDate && (
                  <button
                    type="button"
                    onClick={() => {
                      const nextDate = calculateNextBillingFromAnniversary();
                      setFormData(prev => ({ ...prev, nextBillingDate: nextDate }));
                    }}
                    className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-md transition-colors"
                  >
                    Calculer depuis l'anniversaire
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="contractType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Type de contrat
                  </label>
                  <input
                    type="text"
                    id="contractType"
                    name="contractType"
                    value={formData.contractType}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="TMA Annuelle, TMA Trimestrielle, etc."
                  />
                </div>
                
                <div>
                  <label htmlFor="billingPeriodicity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Périodicité de facturation
                  </label>
                  <select
                    id="billingPeriodicity"
                    name="billingPeriodicity"
                    value={formData.billingPeriodicity || 'non_specifie'}
                    onChange={handleChange}
                    className="input-field"
                  >
                    <option value="non_specifie">Non spécifié</option>
                    {BILLING_PERIODICITY_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.billingPeriodicity === 'autre' && (
                  <div>
                    <label htmlFor="billingPeriodicityDays" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nombre de jours
                    </label>
                    <input
                      type="number"
                      id="billingPeriodicityDays"
                      name="billingPeriodicityDays"
                      value={formData.billingPeriodicityDays || 30}
                      onChange={handleChange}
                      min="1"
                      className="input-field"
                      placeholder="30"
                    />
                  </div>
                )}

                <div>
                  <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Montant (€)
                  </label>
                  <input
                    type="number"
                    id="amount"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="input-field"
                  />
                </div>

                <div>
                  <label htmlFor="anniversaryDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date d'anniversaire du contrat
                  </label>
                  <input
                    type="date"
                    id="anniversaryDate"
                    name="anniversaryDate"
                    value={formData.anniversaryDate}
                    onChange={handleChange}
                    className="input-field"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Date de référence pour calculer les prochaines facturations
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="lastBillingDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Dernière facturation
                  </label>
                  <input
                    type="date"
                    id="lastBillingDate"
                    name="lastBillingDate"
                    value={formData.lastBillingDate}
                    onChange={handleChange}
                    className="input-field"
                  />
                </div>

                <div>
                  <label htmlFor="nextBillingDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Prochaine facturation
                  </label>
                  <input
                    type="date"
                    id="nextBillingDate"
                    name="nextBillingDate"
                    value={formData.nextBillingDate}
                    onChange={handleChange}
                    className="input-field"
                  />
                </div>

                <div>
                  <label htmlFor="paymentStatus" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Statut de paiement
                  </label>
                  <select
                    id="paymentStatus"
                    name="paymentStatus"
                    value={formData.paymentStatus}
                    onChange={handleChange}
                    className="input-field"
                  >
                    <option value="">Sélectionner un statut</option>
                    {PAYMENT_STATUS_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                                 {/* Informations calculées */}
                 {(formData.nextBillingDate || formData.anniversaryDate) && (
                   <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                     <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Informations calculées</h4>
                     <div className="space-y-1 text-sm">
                       {formData.anniversaryDate && (
                         <div className="flex justify-between">
                           <span className="text-gray-600 dark:text-gray-400">Date d'anniversaire :</span>
                           <span className="font-medium">
                             {new Date(formData.anniversaryDate).toLocaleDateString()}
                           </span>
                         </div>
                       )}
                       {formData.nextBillingDate && (
                         <div className="flex justify-between">
                           <span className="text-gray-600 dark:text-gray-400">Jours restants :</span>
                           <span className="font-medium">
                             {(() => {
                               const today = new Date();
                               const nextBilling = new Date(formData.nextBillingDate);
                               const diffTime = nextBilling.getTime() - today.getTime();
                               const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                               return diffDays > 0 ? `${diffDays} jours` : `${Math.abs(diffDays)} jours de retard`;
                             })()}
                           </span>
                         </div>
                       )}
                       <div className="flex justify-between">
                         <span className="text-gray-600 dark:text-gray-400">Périodicité :</span>
                         <span className="font-medium">
                           {BILLING_PERIODICITY_OPTIONS.find(opt => opt.value === formData.billingPeriodicity)?.label}
                           {formData.billingPeriodicity === 'autre' && formData.billingPeriodicityDays && ` (${formData.billingPeriodicityDays} jours)`}
                         </span>
                       </div>
                     </div>
                   </div>
                 )}

                 {/* Historique des paiements */}
                 {formData.paymentHistory && formData.paymentHistory.length > 0 && (
                   <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                     <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Historique des paiements</h4>
                     <div className="space-y-2 max-h-40 overflow-y-auto">
                       {formData.paymentHistory.slice(-5).reverse().map((entry) => (
                         <div key={entry.id} className="flex justify-between items-center text-sm p-2 bg-white dark:bg-gray-700 rounded">
                           <div>
                             <span className="font-medium">{new Date(entry.date).toLocaleDateString()}</span>
                             {entry.notes && <span className="text-gray-500 ml-2">- {entry.notes}</span>}
                           </div>
                           <div className="flex items-center space-x-2">
                             <span className="font-medium">{entry.amount} €</span>
                             <span className={`px-2 py-1 text-xs rounded-full ${
                               entry.status === 'payé' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                               entry.status === 'en_attente' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                               entry.status === 'en_retard' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' :
                               'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                             }`}>
                               {entry.status}
                             </span>
                           </div>
                         </div>
                       ))}
                     </div>
                   </div>
                 )}
              </div>
            </div>
          </div>
        )}

        {/* Domaine et DNS */}
        {activeTab === 'domain' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="domain" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nom de domaine (généré depuis l'URL)
                </label>
                <input
                  type="text"
                  id="domain"
                  name="domain"
                  value={formData.domain}
                  readOnly
                  disabled
                  className="input-field bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
                  placeholder="example.com"
                />
              </div>
              <div>
                <label htmlFor="domainExpiration" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Expiration du domaine
                </label>
                <input
                  type="date"
                  id="domainExpiration"
                  name="domainExpiration"
                  value={formData.domainExpiration}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="registrar" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Registrar
                </label>
                <input
                  type="text"
                  id="registrar"
                  name="registrar"
                  value={formData.registrar}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="OVH, Gandi, etc."
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="cloudflareProxy"
                  name="cloudflareProxy"
                  checked={formData.dnsConfig.cloudflareProxy}
                  onChange={handleDnsConfigChange}
                  className="form-checkbox"
                />
                <label htmlFor="cloudflareProxy" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Cloudflare Proxy
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="cacheHeaders"
                  name="cacheHeaders"
                  checked={formData.dnsConfig.cacheHeaders}
                  onChange={handleDnsConfigChange}
                  className="form-checkbox"
                />
                <label htmlFor="cacheHeaders" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Cache Headers
                </label>
              </div>
              
              {/* Protection htaccess */}
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium mb-4">Informations htaccess</h3>
                
                <div className="flex items-center space-x-2 mb-4">
                  <input
                    type="checkbox"
                    id="htaccessEnabled"
                    name="htaccessEnabled"
                    checked={formData.htaccessEnabled}
                    onChange={(e) => setFormData({
                      ...formData,
                      htaccessEnabled: e.target.checked
                    })}
                    className="form-checkbox"
                  />
                  <label htmlFor="htaccessEnabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Site protégé par htaccess
                  </label>
                </div>
                
                {formData.htaccessEnabled && (
                  <>
                    <div className="mb-4">
                      <label htmlFor="htaccessUsername" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nom d'utilisateur
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          id="htaccessUsername"
                          name="htaccessUsername"
                          value={formData.htaccessUsername}
                          onChange={handleChange}
                          className="input-field"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            // Générer un nom d'utilisateur à partir du nom du site
                            try {
                              if (formData.url) {
                                const url = new URL(formData.url);
                                const domain = url.hostname.replace('www.', '');
                                const siteName = domain.split('.')[0];
                                setFormData({
                                  ...formData,
                                  htaccessUsername: siteName
                                });
                              }
                            } catch (error) {
                              console.error("URL invalide", error);
                            }
                          }}
                          className="btn-secondary"
                          title="Générer un nom d'utilisateur à partir du nom du site"
                        >
                          Générer
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="htaccessPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Mot de passe
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type="text" // Utilisez text au lieu de password pour que l'utilisateur puisse voir le mot de passe généré
                          id="htaccessPassword"
                          name="htaccessPassword"
                          value={formData.htaccessPassword}
                          onChange={handleChange}
                          className="input-field"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            // Utiliser le même mot de passe que le nom d'utilisateur
                            setFormData({
                              ...formData,
                              htaccessPassword: formData.htaccessUsername
                            });
                          }}
                          className="btn-secondary"
                          title="Utiliser le nom d'utilisateur comme mot de passe"
                        >
                          Générer
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Emails et SMTP */}
        {activeTab === 'email' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="emailType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type d'email
                </label>
                <select
                  id="emailType"
                  name="emailType"
                  value={formData.emailType}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="smtp">SMTP</option>
                  <option value="postal-smtp">Postal SMTP</option>
                  <option value="postal-http">Postal HTTP</option>
                  <option value="autre">Autre</option>
                  <option value="aucun">Sans email</option>
                </select>
              </div>
              
              {/* Champs pour SMTP */}
              {formData.emailType === 'smtp' && (
                <>
                  <div>
                    <label htmlFor="smtpServer" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Serveur SMTP
                    </label>
                    <input
                      type="text"
                      id="smtpServer"
                      name="smtpServer"
                      value={formData.smtpServer}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="mail.example.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="smtpEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Adresse email
                    </label>
                    <input
                      type="email"
                      id="smtpEmail"
                      name="smtpEmail"
                      value={formData.smtpEmail}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="contact@example.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="smtpPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Mot de passe
                    </label>
                    <input
                      type="password"
                      id="smtpPassword"
                      name="smtpPassword"
                      value={formData.smtpPassword}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="••••••••"
                    />
                  </div>
                  <div>
                    <label htmlFor="smtpPort" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Port SMTP
                    </label>
                    <input
                      type="number"
                      id="smtpPort"
                      name="smtpPort"
                      value={formData.smtpPort}
                      onChange={handleChange}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label htmlFor="encryption" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Chiffrement
                    </label>
                    <select
                      id="encryption"
                      name="encryption"
                      value={formData.encryption}
                      onChange={handleChange}
                      className="input-field"
                    >
                      <option value="SSL">SSL</option>
                      <option value="TLS">TLS</option>
                      <option value="STARTTLS">STARTTLS</option>
                      <option value="">Aucun</option>
                    </select>
                  </div>
                </>
              )}
              
              {/* Champs pour Postal SMTP */}
              {formData.emailType === 'postal-smtp' && (
                <>
                  <div>
                    <label htmlFor="smtpServer" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Serveur SMTP
                    </label>
                    <input
                      type="text"
                      id="smtpServer"
                      name="smtpServer"
                      value={formData.smtpServer}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="mail.example.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="smtpEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Adresse email
                    </label>
                    <input
                      type="email"
                      id="smtpEmail"
                      name="smtpEmail"
                      value={formData.smtpEmail}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="contact@example.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="smtpPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Mot de passe
                    </label>
                    <input
                      type="password"
                      id="smtpPassword"
                      name="smtpPassword"
                      value={formData.smtpPassword}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="••••••••"
                    />
                  </div>
                  <div>
                    <label htmlFor="smtpPort" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Port SMTP
                    </label>
                    <input
                      type="number"
                      id="smtpPort"
                      name="smtpPort"
                      value={formData.smtpPort}
                      onChange={handleChange}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label htmlFor="encryption" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Chiffrement
                    </label>
                    <select
                      id="encryption"
                      name="encryption"
                      value={formData.encryption}
                      onChange={handleChange}
                      className="input-field"
                    >
                      <option value="SSL">SSL</option>
                      <option value="TLS">TLS</option>
                      <option value="STARTTLS">STARTTLS</option>
                      <option value="">Aucun</option>
                    </select>
                  </div>
                </>
              )}
              
              {/* Champs pour Postal HTTP */}
              {formData.emailType === 'postal-http' && (
                <>
                  <div>
                    <label htmlFor="postalHttpUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      URL du serveur
                    </label>
                    <input
                      type="text"
                      id="postalHttpUrl"
                      name="postalHttpUrl"
                      value={formData.postalHttpUrl}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="https://postal.example.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="postalHttpApiKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Clé API
                    </label>
                    <input
                      type="text"
                      id="postalHttpApiKey"
                      name="postalHttpApiKey"
                      value={formData.postalHttpApiKey}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="api-key"
                    />
                  </div>
                  <div>
                    <label htmlFor="postalHttpSenderEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email d'expéditeur
                    </label>
                    <input
                      type="email"
                      id="postalHttpSenderEmail"
                      name="postalHttpSenderEmail"
                      value={formData.postalHttpSenderEmail}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="noreply@example.com"
                    />
                  </div>
                </>
              )}
              
              {/* Champ de note pour le type "autre" */}
              {formData.emailType === 'autre' && (
                <div>
                  <label htmlFor="emailNote" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes sur la configuration email
                  </label>
                  <textarea
                    id="emailNote"
                    name="emailNote"
                    value={formData.emailNote}
                    onChange={handleChange}
                    className="input-field h-24"
                    placeholder="Détails sur la configuration email utilisée..."
                  ></textarea>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Maintenance */}
        {activeTab === 'maintenance' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="backupType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type de backup
                </label>
                <select
                  id="backupType"
                  name="backupType"
                  value={formData.backupType}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="minio">Bucket Minio</option>
                  <option value="docker">Serveur Docker</option>
                  <option value="autre">Autre</option>
                  <option value="aucun">Sans backup</option>
                </select>
              </div>
              
              {/* Champs spécifiques pour le type Minio */}
              {formData.backupType === 'minio' && (
                <>
                  <div>
                    <label htmlFor="bucketName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nom du bucket
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        id="bucketName"
                        name="bucketName"
                        value={formData.bucketName}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="up-nomdusite"
                      />
                      <button
                        type="button"
                        onClick={generateBucketName}
                        className="btn-secondary"
                      >
                        Générer le nom du bucket
                      </button>
                    </div>
                    
                    {/* Bouton pour créer le bucket */}
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={handleCreateBucket}
                        disabled={creatingBucket || !formData.clientName}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          creatingBucket || !formData.clientName
                            ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 dark:bg-blue-700 text-white hover:bg-blue-700 dark:hover:bg-blue-800'
                        }`}
                      >
                        {creatingBucket ? (
                          <>
                            <ArrowPathIcon className="animate-spin h-4 w-4" />
                            <span>Création en cours...</span>
                          </>
                        ) : (
                          <>
                            <PlusIcon className="h-4 w-4" />
                            <span>Créer le bucket MinIO</span>
                          </>
                        )}
                      </button>
                    </div>

                    {/* Affichage des erreurs */}
                    {bucketError && (
                      <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                        <div className="flex items-center">
                          <ExclamationCircleIcon className="h-5 w-5 text-red-400 dark:text-red-500 mr-2" />
                          <span className="text-sm text-red-700 dark:text-red-300">{bucketError}</span>
                        </div>
                      </div>
                    )}

                    {/* Affichage des credentials du bucket */}
                    {bucketConfig && (
                      <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-green-800 dark:text-green-200">
                            ✅ Bucket créé avec succès
                          </h4>
                          <button
                            type="button"
                            onClick={() => setShowBucketCredentials(!showBucketCredentials)}
                            className="flex items-center space-x-1 text-sm text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
                          >
                            {showBucketCredentials ? (
                              <>
                                <EyeSlashIcon className="h-4 w-4" />
                                <span>Masquer les clés</span>
                              </>
                            ) : (
                              <>
                                <EyeIcon className="h-4 w-4" />
                                <span>Afficher les clés</span>
                              </>
                            )}
                          </button>
                        </div>
                        
                        {showBucketCredentials && (
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-green-700 dark:text-green-300 mb-1">
                                Nom du bucket
                              </label>
                              <div className="flex space-x-2">
                                <input
                                  type="text"
                                  value={bucketConfig.bucketName}
                                  readOnly
                                  className="flex-1 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 rounded-md font-mono text-gray-900 dark:text-gray-100"
                                />
                                <button
                                  type="button"
                                  onClick={() => copyToClipboard(bucketConfig.bucketName, 'Nom du bucket')}
                                  className="px-3 py-2 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 text-green-700 dark:text-green-300 rounded-md transition-colors"
                                  title="Copier le nom du bucket"
                                >
                                  <ClipboardDocumentIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>

                            {/* Clé d'accès S3 */}
                            {bucketConfig.accessKey && (
                              <div>
                                <label className="block text-xs font-medium text-green-700 dark:text-green-300 mb-1">
                                  Clé d'accès S3
                                </label>
                                <div className="flex space-x-2">
                                  <input
                                    type="text"
                                    value={bucketConfig.accessKey}
                                    readOnly
                                    className="flex-1 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 rounded-md font-mono text-gray-900 dark:text-gray-100"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => copyToClipboard(bucketConfig.accessKey, 'Clé d\'accès S3')}
                                    className="px-3 py-2 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 text-green-700 dark:text-green-300 rounded-md transition-colors"
                                    title="Copier la clé d'accès"
                                  >
                                    <ClipboardDocumentIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Clé secrète S3 */}
                            {bucketConfig.secretKey && (
                              <div>
                                <label className="block text-xs font-medium text-green-700 dark:text-green-300 mb-1">
                                  Clé secrète S3
                                </label>
                                <div className="flex space-x-2">
                                  <input
                                    type="text"
                                    value={bucketConfig.secretKey}
                                    readOnly
                                    className="flex-1 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 rounded-md font-mono text-gray-900 dark:text-gray-100"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => copyToClipboard(bucketConfig.secretKey, 'Clé secrète S3')}
                                    className="px-3 py-2 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 text-green-700 dark:text-green-300 rounded-md transition-colors"
                                    title="Copier la clé secrète"
                                  >
                                    <ClipboardDocumentIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Point de terminaison S3 */}
                            {bucketConfig.endpoint && (
                              <div>
                                <label className="block text-xs font-medium text-green-700 dark:text-green-300 mb-1">
                                  Point de terminaison S3
                                </label>
                                <div className="flex space-x-2">
                                  <input
                                    type="text"
                                    value={bucketConfig.endpoint}
                                    readOnly
                                    className="flex-1 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 rounded-md font-mono text-gray-900 dark:text-gray-100"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => copyToClipboard(bucketConfig.endpoint, 'Point de terminaison S3')}
                                    className="px-3 py-2 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 text-green-700 dark:text-green-300 rounded-md transition-colors"
                                    title="Copier le point de terminaison"
                                  >
                                    <ClipboardDocumentIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            )}
                            

                            
                            <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                              <div className="flex items-start">
                                <InformationCircleIcon className="h-5 w-5 text-yellow-400 dark:text-yellow-500 mr-2 mt-0.5" />
                                <div className="text-sm text-yellow-700 dark:text-yellow-300">
                                  <p className="font-medium">Informations importantes :</p>
                                  <ul className="mt-1 list-disc list-inside space-y-1">
                                    <li>Sauvegardez ces informations dans un endroit sécurisé</li>
                                    <li>Le bucket a été créé avec le versioning activé</li>
                                    <li>Les anciennes versions expirent après 7 jours</li>
                                    <li>Utilisez ces clés pour configurer vos scripts de backup</li>
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Modal pour afficher les logs */}
                  {showLogsModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            📋 Logs de création du bucket
                          </h3>
                          <button
                            onClick={() => setShowLogsModal(false)}
                            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                          >
                            <XMarkIcon className="h-6 w-6" />
                          </button>
                        </div>
                        
                        <div className="space-y-2">
                          {bucketConfig?.logs?.map((log: any, index: number) => (
                            <div
                              key={index}
                              className={`p-3 rounded-md text-sm ${
                                log.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' :
                                log.type === 'error' ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800' :
                                log.type === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800' :
                                'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                              }`}
                            >
                              {log.message}
                            </div>
                          ))}
                        </div>
                        
                        <div className="mt-6 flex justify-end">
                          <button
                            onClick={() => setShowLogsModal(false)}
                            className="px-4 py-2 bg-gray-600 dark:bg-gray-700 text-white rounded-md hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
                          >
                            Fermer
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    {backupSlotContent}
                    
                    <div>
                      <label htmlFor="backupTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Heure
                      </label>
                      <input
                        type="text"
                        id="backupTime"
                        name="backupTime"
                        value={formData.backupSchedule?.time || ''}
                        disabled
                        className="input-field bg-gray-100 dark:bg-gray-700"
                        placeholder="Automatiquement définie par la tranche"
                      />
                    </div>
                  </div>
                </>
              )}
              
              {/* Champ de note pour le type "autre" */}
              {formData.backupType === 'autre' && (
                <div>
                  <label htmlFor="backupNote" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes sur le backup
                  </label>
                  <textarea
                    id="backupNote"
                    name="backupNote"
                    value={formData.backupNote}
                    onChange={handleChange}
                    className="input-field h-24"
                    placeholder="Détails sur le système de backup utilisé..."
                  ></textarea>
                </div>
              )}
              
              {/* Commentaire de maintenance pour tous les types */}
              <div>
                <label htmlFor="maintenanceComments" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Commentaire de maintenance
                </label>
                <textarea
                  id="maintenanceComments"
                  name="maintenanceComments"
                  value={formData.maintenanceComments}
                  onChange={handleChange}
                  className="input-field h-24"
                  placeholder="Notes concernant la maintenance..."
                ></textarea>
              </div>
            </div>
          </div>
        )}

        {/* Contacts */}
        {activeTab === 'contact' && (
          <div className="grid grid-cols-1 gap-6">
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Contacts</h3>
                <button
                  type="button"
                  onClick={handleAddContact}
                  className="px-3 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded-md flex items-center"
                >
                  <UserPlusIcon className="h-4 w-4 mr-1" />
                  Ajouter un contact
                </button>
              </div>
              
              {formData.contacts && formData.contacts.length > 0 ? (
                <div className="space-y-4">
                  {formData.contacts.map(contact => (
                    <ContactForm
                      key={contact.id}
                      contact={contact}
                      onUpdate={handleUpdateContact}
                      onDelete={() => handleDeleteContact(contact.id)}
                      isPrimary={!!contact.isPrimary}
                      onSetPrimary={() => handleSetPrimaryContact(contact.id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-md">
                  <p className="text-gray-500 dark:text-gray-400 mb-4">Aucun contact ajouté</p>
                  <button
                    type="button"
                    onClick={handleAddContact}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md inline-flex items-center"
                  >
                    <UserPlusIcon className="h-5 w-5 mr-2" />
                    Ajouter votre premier contact
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tags */}
        {activeTab === 'tags' && (
          <div className="grid grid-cols-1 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Tags</h3>
              
              {/* Ajout de nouveaux tags */}
              <div className="mb-6">
                <label htmlFor="newTag" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ajouter un tag
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="newTag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={handleTagKeyPress}
                    className="flex-1 input-field"
                    placeholder="Ex: WordPress, E-commerce, Client VIP..."
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    disabled={!newTag.trim() || (formData.tags || []).includes(newTag.trim())}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md flex items-center"
                  >
                    <TagIcon className="h-4 w-4 mr-1" />
                    Ajouter
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Appuyez sur Entrée ou cliquez sur "Ajouter" pour ajouter le tag
                </p>
              </div>

              {/* Liste des tags existants */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Tags actuels ({(formData.tags || []).length})
                </h4>
                
                {formData.tags && formData.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-100"
                          title={`Supprimer le tag "${tag}"`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-md">
                    <TagIcon className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-3" />
                    <p className="text-gray-500 dark:text-gray-400 mb-2">Aucun tag ajouté</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                      Les tags vous permettent de catégoriser et filtrer vos sites plus facilement
                    </p>
                  </div>
                )}
              </div>

              {/* Suggestions de tags courants */}
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Suggestions de tags
                </h4>
                <div className="flex flex-wrap gap-2">
                  {['WordPress', 'E-commerce', 'Vitrine', 'Blog', 'Client VIP', 'Maintenance', 'Développement', 'Production', 'Test', 'Urgent'].map((suggestedTag) => (
                    <button
                      key={suggestedTag}
                      type="button"
                      onClick={() => {
                        if (!(formData.tags || []).includes(suggestedTag)) {
                          setFormData(prevData => ({
                            ...prevData,
                            tags: [...(prevData.tags || []), suggestedTag]
                          }));
                        }
                      }}
                      disabled={(formData.tags || []).includes(suggestedTag)}
                      className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
                    >
                      + {suggestedTag}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Boutons de soumission */}
      <div className="mt-8 flex justify-end space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="btn-outline"
          disabled={isSubmitting}
        >
          Annuler
        </button>
        <button
          type="submit"
          className="btn-primary flex items-center"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <ArrowPathIcon className="animate-spin h-5 w-5 mr-2" />
              {website.id ? 'Mise à jour en cours...' : 'Création en cours...'}
            </>
          ) : (
            website.id ? 'Mettre à jour' : 'Créer'
          )}
        </button>
      </div>
    </form>
  );
};

export default WebsiteForm; 